<template>
  <div class="tool-tip">
    <transition :name="transition" @after-enter="handleAfterEnter">
      <div
        class="el-popover el-popper"
        :class="[popperClass, content && 'el-popover--plain']"
        ref="popper"
        v-show="!disabled && showPopper"
        :style="{ width: width + 'px' }"
        role="tooltip"
        :aria-hidden="disabled || !showPopper ? 'true' : 'false'"
        :id="tooltipId"
      >
        <div class="el-popover__title" v-if="title" v-text="title"></div>
        <slot />
      </div>
    </transition>
    <slot name="reference" />
  </div>
</template>

<script>
import Vue from "vue";
import { generateId, addClass, removeClass, on, off } from "./util";

export default {
  // mixins: [Popper],
  data() {
    return {
      popperJS: null,
      referenceElm: null,
      popper: null,
      popperArrow: null,
      timer: null,
      showPopper: false
    };
  },
  props: {
    showToggle: { type: Boolean, default: false },
    // 触发方式
    trigger: {
      type: String,
      default: "click",
      validator: value =>
        ["click", "focus", "hover", "manual"].indexOf(value) > -1
    },
    placement: {
      type: String,
      default: "bottom",
      validator: value => ["top", "bottom", "left", "right"].indexOf(value) > -1
    },
    // 触发打开延时
    openDelay: { type: Number, default: 0 },
    title: String,
    disabled: Boolean,
    content: String,
    reference: {},
    popperClass: String,
    width: {},
    visibleArrow: { default: true },
    arrowOffset: { type: Number, default: 0 },
    transition: { type: String, default: "fade-in-linear" },
    offset: Number
  },
  computed: {
    tooltipId() {
      return `el-popover-${generateId()}`;
    }
  },
  watch: {
    showToggle(val) {
      this.showPopper = val;
    },
    showPopper(val) {
      if (val) {
        this.$emit("show");
        this.createPopper();
      } else {
        this.$emit("hide");
        this.doDestroyEl();
      }
    }
  },
  mounted() {
    let reference = (this.referenceElm =
      this.reference || this.$refs.reference);
    const popper = (this.popper = this.popper || this.$refs.popper);

    if (!reference && this.$slots.reference && this.$slots.reference[0]) {
      reference = this.referenceElm = this.$slots.reference[0].elm;
      popper.setAttribute("x-placement", this.placement);
    }

    if (this.visibleArrow) {
      let popperArrow = (this.popperArrow = document.createElement("div"));

      popperArrow.setAttribute("class", "popper__arrow");
      popper.appendChild(popperArrow);
    }

    if (reference) {
      addClass(reference, "el-popover__reference");
      reference.setAttribute("aria-describedby", this.tooltipId);
      reference.setAttribute("tabindex", 0);
      popper.setAttribute("tabindex", 0);

      if (this.trigger !== "click") {
        on(reference, "focusin", () => {
          this.handleFocus();
          const instance = reference.__vue__;
          if (instance && typeof instance.focus === "function") {
            instance.focus();
          }
        });

        on(popper, "focusin", this.handleFocus);
        on(reference, "focusout", this.handleBlur);
        on(popper, "focusout", this.handleBlur);
      }

      on(reference, "Keydown", this.handleKeydown);
      on(reference, "click", this.handleClick);
    }
    if (this.trigger === "click") {
      on(reference, "click", this.doToggle);
      // on(document, 'click', this.handleDocumentClick)
    } else if (this.trigger === "hover") {
      on(reference, "mouseenter", this.handleMouseEnter);
      on(popper, "mouseenter", this.handleMouseEnter);
      on(reference, "mouseleave", this.handleMouseLeave);
      on(popper, "mouseleave", this.handleMouseLeave);
    } else if (this.trigger === "focus") {
      let found = false;

      if ([].slice.call(reference.children).length) {
        const children = reference.childNodes;
        const len = children.length;

        for (let i = 0; i < len; i++) {
          if (
            children[i].nodeName === "INPUT" ||
            children[i].nodeName === "TEXTAREA"
          ) {
            on(children[i], "focusin", this.doShow);
            on(children[i], "focusout", this.doClose);
            found = true;
            break;
          }
        }
      }

      if (found) return;

      if (reference.nodeName === "INPUT" || reference.nodeName === "TEXTAREA") {
        on(reference, "focusin", this.doShow);
        on(reference, "focusout", this.doClose);
      } else {
        on(reference, "mousedown", this.doShow);
        on(reference, "mouseUp", this.doClose);
      }
    }
  },
  methods: {
    createPopper: function() {
      document.body.appendChild(this.popper);
      this.$nextTick(() => {
        let elRect = document.documentElement.getBoundingClientRect();
        let popperRect = this.popper.getBoundingClientRect();
        let referenceRect = this.referenceElm.getBoundingClientRect();

        // 解决 getBoundingClientRect x,y兼容性
        let { x, y } = referenceRect;
        if (x === undefined || y === undefined) {
          x = referenceRect.left;
          y = referenceRect.top;
        }

        let popperLeft, popperTop, arrowTop, arrowLeft;
        if (this.placement === "left") {
          popperLeft = x - popperRect.width - 10;
          popperTop = y + referenceRect.height / 2 - popperRect.height / 2;

          popperTop = this.offset ? popperTop - this.offset : popperTop;

          if (popperTop < 0) {
            popperTop = 0;
          } else if (popperTop + popperRect.height > elRect.height) {
            popperTop = elRect.height - popperRect.height - 10;
          }
          arrowTop = y - 6 - popperTop + referenceRect.height / 2;
          this.popperArrow.setAttribute("style", `top: ${arrowTop}px`);
          this.popper.setAttribute(
            "style",
            `width: ${this.width}px; top: ${popperTop}px; left: ${popperLeft}px; z-index: 1001;`
          );
        }
      });
    },
    doDestroyEl: function() {
      if (document.body.hasChildNodes(this.popper)) {
        document.body.removeChild(this.popper);
      }
    },
    handleMouseLeave: function() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.showPopper = false;
      }, 200);
    },
    handleMouseEnter: function() {
      clearTimeout(this.timer);
      if (this.openDelay) {
        this.timer = setTimeout(() => {
          this.showPopper = true;
        }, this.openDelay);
      } else {
        this.showPopper = true;
      }
    },
    handleDocumentClick: function(el) {
      let reference = this.reference || this.$refs.reference;
      const popper = this.popper || this.$refs.popper;

      if (!reference && this.$slots.reference && this.$slots.reference[0]) {
        reference = this.referenceElm = this.$slots.reference[0].elm;
      }
      if (
        !this.$el ||
        !reference ||
        this.$el.contains(el.target) ||
        reference.contains(el.target) ||
        !popper ||
        popper.contains(el.target)
      )
        return;
      this.showPopper = false;
    },
    handleClick: function() {
      removeClass(this.referenceElm, "focusing");
    },
    handleKeydown: function(el) {
      if (el.keyCode === 27 && this.trigger !== "manual") {
        this.doClose();
      }
    },
    handleBlur: function() {
      removeClass(this.referenceElm, "focusing");
      if (this.trigger !== "manual") this.showPopper = false;
    },
    handleFocus: function() {
      addClass(this.referenceElm, "focusing");
      if (this.trigger !== "manual") this.showPopper = true;
    },
    doClose: function() {
      this.showPopper = false;
    },
    doToggle: function() {
      this.showPopper = !this.showPopper;
    },
    doShow: function() {
      this.showPopper = true;
    },
    handleAfterEnter() {
      this.$emit("after-enter");
    },
    removeElement(_element) {
      let _parentElement = _element.parentNode;
      if (_parentElement) {
        _parentElement.removeChild(_element);
      }
    }
  },

  destroyed() {
    const reference = this.referenceElm || this.reference;
    this.removeElement(this.popper); // 兼容ie处理
    // this.popper.remove?this.popper.remove():this.popper.removeNode?this.popper.removeNode():''
    off(reference, "click", this.doToggle);
    off(reference, "mouseup", this.doClose);
    off(reference, "mousedown", this.doShow);
    off(reference, "focusin", this.doShow);
    off(reference, "focusout", this.doClose);
    off(reference, "mouseleave", this.handleMouseLeave);
    off(reference, "mouseenter", this.handleMouseEnter);
    off(document, "click", this.handleDocumentClick);
  },
  name: "tool-tip"
};
</script>

<style scoped>
.tool-tip {
  display: inline-block;
  float: left;
}
</style>



// WEBPACK FOOTER //
// components/tool-tip/index.vue